import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CountUp from "react-countup";

const WhyUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const statistics = [
    {
      label: "Guests hosted",
      value: 30000,
      suffix: "+",
    },
    {
      label: "Return Guests",
      value: 60,
      suffix: "%",
    },
    {
      label: "Ratings",
      value: 4.8,
      suffix: "/5",
      decimals: 1,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        position: "relative",
        py: { xs: 6, sm: 8, md: 10 },
        backgroundImage: "url(/why-us-bg.png)", // You'll need to add this image
        backgroundSize: "cover",
        backgroundPosition: "center right",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        borderRadius: 4,
        mx: { xs: 1, sm: 2 },
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isMobile && "rgba(0, 0, 0, 0.4)", // Dark overlay
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 6, md: 8 },
        }}
      >
        <Grid container spacing={6} alignItems="center">
          {/* Left Side - Content */}
          <Grid item size={{xs: 12, md: 8}}>
            <Box sx={{ color: "white" }}>
              {/* Category Label */}
              <Typography
                data-aos="fade-up"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontFamily: "Monument-Private",
                  fontWeight: 500,
                  letterSpacing: 2,
                  fontSize: "0.9rem",
                  mb: 3,
                }}
              >
                WHY US?
              </Typography>

              {/* Main Heading */}
              <Typography
                variant="h2"
                component="h2"
                data-aos="fade-up"
                data-aos-delay="200"
                sx={{
                  fontFamily: "Monument-Private",
                  fontSize: {
                    xs: "2.5rem",
                    sm: "3rem",
                    md: "3.5rem",
                  },
                  lineHeight: 1.1,
                  mb: 4,
                  fontWeight: "bold",
                  color: "white",
                }}
              >
                Why guests trust Aspire Ventures
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                data-aos="fade-up"
                data-aos-delay="400"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                  mb: 6,
                }}
              >
                Aspire Ventures is dedicated to providing exceptional hospitality, local expertise, and seamless stays. Our team ensures every guest enjoys a safe, comfortable, and truly Goan experience—every single time.
              </Typography>

              {/* Statistics */}
              <Grid container spacing={4} direction={{ xs: "column", sm: "row" }}>
                {statistics.map((stat, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      sx={{
                        borderLeft: {
                          xs: "none",
                          sm: index > 0 ? "1px solid rgba(255, 255, 255, 0.2)" : "none"
                        },
                        borderTop: {
                          xs: index > 0 ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
                          sm: "none"
                        },
                        pl: {
                          xs: 0,
                          sm: index > 0 ? 4 : 0
                        },
                        pt: {
                          xs: index > 0 ? 3 : 0,
                          sm: 0
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(255, 255, 255, 0.6)",
                          fontFamily: "Monument-Private",
                          fontSize: "0.9rem",
                          mb: 1,
                          textTransform: "capitalize",
                        }}
                      >
                        {stat.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: "white",
                          fontFamily: "Monument-Private",
                          fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                          lineHeight: 1,
                        }}
                      >
                        {isVisible ? (
                          <CountUp
                            start={0}
                            end={stat.value}
                            duration={2.5}
                            delay={index * 0.2}
                            decimals={stat.decimals || 0}
                            decimal="."
                          />
                        ) : (
                          "0"
                        )}
                        {stat.suffix && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "1.5rem",
                              fontFamily: "Monument-Private",
                              color: "rgba(255, 255, 255, 0.7)",
                              fontWeight: 400,
                            }}
                          >
                            {stat.suffix}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyUs;
